import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker?url";
import mammoth from "mammoth";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

export const extractResumeTextFromFile = async (file: File): Promise<string> => {
  const name = file.name.toLowerCase();

  // TXT
  if (name.endsWith(".txt")) {
    return await file.text();
  }

  // PDF
  if (name.endsWith(".pdf")) {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    // Resumes should typically be 1–2 pages; cap work for huge PDFs to keep UX fast.
    const maxPages = Math.min(pdf.numPages, 6);

    const pageTexts = await Promise.all(
      Array.from({ length: maxPages }, async (_, i) => {
        const page = await pdf.getPage(i + 1);
        const content = await page.getTextContent();
        const items: any[] = (content as any).items ?? [];

        // Preserve line breaks by grouping items by Y coordinate.
        const positioned = items
          .map((it) => {
            const str = typeof it?.str === "string" ? it.str : "";
            const x = Array.isArray(it?.transform) ? Number(it.transform[4]) : 0;
            const y = Array.isArray(it?.transform) ? Number(it.transform[5]) : 0;
            return { str, x, y };
          })
          .filter((it) => it.str.trim().length > 0);

        // Sort top-to-bottom then left-to-right
        positioned.sort((a, b) => (b.y - a.y) || (a.x - b.x));

        const lines: string[] = [];
        let currentY: number | null = null;
        let current = "";

        const flush = () => {
          const s = current.trim();
          if (s) lines.push(s);
          current = "";
        };

        for (const it of positioned) {
          const y = Math.round(it.y); // reduce noise
          if (currentY === null) currentY = y;

          // New line when Y changes meaningfully
          if (Math.abs(y - currentY) > 2) {
            flush();
            currentY = y;
          }

          // Add spacing between tokens
          const token = it.str.replace(/\s+/g, " ").trim();
          if (!token) continue;

          if (current.length === 0) {
            current = token;
          } else {
            // Avoid double spaces around punctuation
            const needsSpace = !/[\(\[\{\/\-]$/.test(current) && !/^[\)\]\}\,\.\:;\!\?]/.test(token);
            current += needsSpace ? ` ${token}` : token;
          }
        }

        flush();
        return lines.join("\n");
      })
    );

    const fullText = pageTexts.filter(Boolean).join("\n\n");
    return fullText;
  }

  // DOCX
  if (name.endsWith(".docx")) {
    const arrayBuffer = await file.arrayBuffer();
    const { value } = await mammoth.extractRawText({ arrayBuffer });
    return value;
  }

  // Unsupported
  throw new Error("Unsupported file type. Please upload a PDF, DOCX, or TXT file.");
};
